import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Checkbox as MUICheckbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { toast } from "react-toastify";
import { Button, Typography } from "Atoms";
import AttachmentManager from "Atoms/AttachmentManager";
import DeletePopUp from "Components/DeletePopUp";
import {
  deleteAttachmentsAPI,
  fetchTestReports,
  postGenerateTestReport,
  TestReportGenerateOption,
} from "Zustand/services/attachmentService";
import { updateTestReportAPI } from "Zustand/services/reportServices";
import { useBoundStore } from "Zustand/store/mainStore";
import { getAttachmentChanges, IMAGE_EXTS, PDF_EXTS } from "Utils/helperFunctions";

const REPORT_TEMPLATE_SPECIFIER = "testreporttemplate";
const DUT_IMAGE_SPECIFIER = "dutimage";
const TEST_REPORT_APPROVAL_SPECIFIER = "testreportapproval";
const DEFAULT_REPORT_OPTIONS: TestReportGenerateOption[] = [
  { identifier: "splitgraphs", label: "Split Graphs", value: false },
];

type ApiAttachment = {
  attachment: {
    title: string;
    description: string;
    specifier?: string;
    file: {
      fileid: string;
      fileversion: string;
      url: string;
      mimetype?: string;
    };
    nodeid: string;
  };
};

type AttachmentAtom = {
  id: string;
  title: string;
  description: string;
  file?: File;
  url?: string;
  mimetype?: string;
  nodeid?: string;
  specifier?: string;
};

function normalizeAttachments(
  apiItems: ApiAttachment[] | undefined
): AttachmentAtom[] {
  if (!apiItems || apiItems.length === 0) return [];

  return apiItems.map((it) => ({
    id: it.attachment.file.fileid,
    title:
      it.attachment.title ||
      it.attachment.file.fileid.split("/").pop() ||
      "untitled",
    description: it.attachment.description || "",
    url: it.attachment.file.url,
    mimetype: it.attachment.file.mimetype,
    nodeid: it.attachment.nodeid,
    specifier: it.attachment.specifier || "",
  }));
}

function normalizeOptions(rawOptions: unknown): TestReportGenerateOption[] {
  const source = Array.isArray(rawOptions) ? rawOptions : [];

  // Backend owns the options list; this fallback keeps the screen usable until
  // older reports start returning options, and moves Split Graphs out of the
  // sticky generate area into the new Options tab.
  const normalized = source
    .map((option: any) => ({
      identifier: String(option?.identifier ?? option?.id ?? option?.name ?? ""),
      label: String(
        option?.label ?? option?.name ?? option?.identifier ?? option?.id ?? ""
      ),
      value: Boolean(option?.value),
    }))
    .filter((option) => option.identifier);

  const hasSplitGraphs = normalized.some(
    (option) => option.identifier.toLowerCase() === "splitgraphs"
  );

  return hasSplitGraphs
    ? normalized
    : [...DEFAULT_REPORT_OPTIONS, ...normalized];
}

export default function Attachments({ reportnodeid }: { reportnodeid: string }) {
  const { uploadAttachments } = useBoundStore();

  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attachments, setAttachments] = useState<AttachmentAtom[]>([]);
  const [options, setOptions] = useState<TestReportGenerateOption[]>(
    DEFAULT_REPORT_OPTIONS
  );

  // DUT images tab
  const [dutImageUploads, setDutImageUploads] = useState<AttachmentAtom[]>([]);
  const [uploadingDutImages, setUploadingDutImages] = useState(false);
  const [dutImageDeleteTarget, setDutImageDeleteTarget] =
    useState<AttachmentAtom | null>(null);
  const [deletingDutImage, setDeletingDutImage] = useState(false);

  // Approval documents tab
  const [approvalUploads, setApprovalUploads] = useState<AttachmentAtom[]>([]);
  const [uploadingApproval, setUploadingApproval] = useState(false);
  const [approvalDeleteTarget, setApprovalDeleteTarget] =
    useState<AttachmentAtom | null>(null);
  const [deletingApproval, setDeletingApproval] = useState(false);

  // Acceptance criteria tab
  const [acceptanceCriteria, setAcceptanceCriteria] = useState("");
  const [savingAcceptanceCriteria, setSavingAcceptanceCriteria] =
    useState(false);

  const reportDocuments = useMemo(
    () =>
      attachments.filter(
        (attachment) =>
          attachment.specifier !== REPORT_TEMPLATE_SPECIFIER &&
          attachment.specifier !== DUT_IMAGE_SPECIFIER &&
          attachment.specifier !== TEST_REPORT_APPROVAL_SPECIFIER
      ),
    [attachments]
  );

  const dutImages = useMemo(
    () =>
      attachments.filter(
        (attachment) => attachment.specifier === DUT_IMAGE_SPECIFIER
      ),
    [attachments]
  );

  // Existing (already-uploaded) DUT images + not-yet-uploaded ones, shown as
  // a single manager instead of two separate "uploaded"/"upload" panels.
  const combinedDutAttachments = useMemo(
    () => [...dutImages, ...dutImageUploads],
    [dutImages, dutImageUploads]
  );

  const approvalDocuments = useMemo(
    () =>
      attachments.filter(
        (attachment) => attachment.specifier === TEST_REPORT_APPROVAL_SPECIFIER
      ),
    [attachments]
  );

  // Existing (already-uploaded) approval documents + not-yet-uploaded ones,
  // shown as a single manager instead of two separate "uploaded"/"upload" panels.
  const combinedApprovalAttachments = useMemo(
    () => [...approvalDocuments, ...approvalUploads],
    [approvalDocuments, approvalUploads]
  );

  const loadReport = useCallback(async () => {
    setError(null);
    setLoading(true);

    try {
      const res = await fetchTestReports(reportnodeid);
      const report = res?.testreport;
      const normalizedAttachments = normalizeAttachments(report?.hasattachment);

      setAttachments(normalizedAttachments);
      setOptions(normalizeOptions(report?.options ?? report?.testreportoptions));
      setAcceptanceCriteria(report?.acceptancecriteria ?? "");
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to load test report documents.");
    } finally {
      setLoading(false);
    }
  }, [reportnodeid]);

  const handleDutAttachmentsChange = useCallback(
    async (updated: AttachmentAtom[]) => {
      const { toEdit, toDelete } = getAttachmentChanges(
        combinedDutAttachments,
        updated
      );

      // Existing items never carry a `file` (disableFileChangeForExisting
      // blocks replacing them), so this isolates newly staged uploads.
      setDutImageUploads(updated.filter((item) => item.file));

      if (toDelete.length > 0) {
        // Don't delete yet — the item stays visible (it's re-derived from
        // `dutImages`, untouched above) until the confirm dialog resolves.
        setDutImageDeleteTarget(toDelete[0] as AttachmentAtom);
      }

      if (toEdit.length > 0) {
        try {
          await uploadAttachments(reportnodeid, [], toEdit);
          toast.success("DUT image updated.");
          await loadReport();
        } catch (e: any) {
          console.error("Failed to update DUT image", e);
          toast.error(e?.message || "Failed to update DUT image.");
        }
      }
    },
    [combinedDutAttachments, uploadAttachments, reportnodeid, loadReport]
  );

  const handleConfirmDutImageDelete = useCallback(async () => {
    if (!dutImageDeleteTarget?.nodeid) {
      setDutImageDeleteTarget(null);
      return;
    }

    setDeletingDutImage(true);
    try {
      await deleteAttachmentsAPI([dutImageDeleteTarget.nodeid]);
      toast.success("DUT image deleted.");
      setDutImageDeleteTarget(null);
      await loadReport();
    } catch (e: any) {
      console.error("Failed to delete DUT image", e);
      toast.error(e?.message || "Failed to delete DUT image.");
    } finally {
      setDeletingDutImage(false);
    }
  }, [dutImageDeleteTarget, loadReport]);

  const handleDutImageUpload = useCallback(async () => {
    const uploadable = dutImageUploads.filter((attachment) => attachment.file);

    if (!uploadable.length) {
      toast.error("Please choose an image to upload.");
      return;
    }

    setUploadingDutImages(true);
    try {
      // DUT images are regular attachments, tagged with a specifier so they
      // can be listed separately from generated report documents.
      const uploadsWithSpecifier = uploadable.map((attachment) => ({
        ...attachment,
        specifier: DUT_IMAGE_SPECIFIER,
        file: Object.assign(attachment.file as File, {
          title: attachment.title,
          description: attachment.description,
          specifier: DUT_IMAGE_SPECIFIER,
        }),
      }));

      await uploadAttachments(reportnodeid, uploadsWithSpecifier, []);
      setDutImageUploads([]);
      toast.success("DUT image uploaded.");
      await loadReport();
    } catch (e: any) {
      console.error("Failed to upload DUT image", e);
      toast.error(e?.message || "Failed to upload DUT image.");
    } finally {
      setUploadingDutImages(false);
    }
  }, [dutImageUploads, uploadAttachments, reportnodeid, loadReport]);

  const handleApprovalAttachmentsChange = useCallback(
    async (updated: AttachmentAtom[]) => {
      const { toEdit, toDelete } = getAttachmentChanges(
        combinedApprovalAttachments,
        updated
      );

      // Existing items never carry a `file` (disableFileChangeForExisting
      // blocks replacing them), so this isolates newly staged uploads.
      setApprovalUploads(updated.filter((item) => item.file));

      if (toDelete.length > 0) {
        // Don't delete yet — the item stays visible (it's re-derived from
        // `approvalDocuments`, untouched above) until the confirm dialog
        // resolves.
        setApprovalDeleteTarget(toDelete[0] as AttachmentAtom);
      }

      if (toEdit.length > 0) {
        try {
          await uploadAttachments(reportnodeid, [], toEdit);
          toast.success("Approval document updated.");
          await loadReport();
        } catch (e: any) {
          console.error("Failed to update approval document", e);
          toast.error(e?.message || "Failed to update approval document.");
        }
      }
    },
    [combinedApprovalAttachments, uploadAttachments, reportnodeid, loadReport]
  );

  const handleConfirmApprovalDelete = useCallback(async () => {
    if (!approvalDeleteTarget?.nodeid) {
      setApprovalDeleteTarget(null);
      return;
    }

    setDeletingApproval(true);
    try {
      await deleteAttachmentsAPI([approvalDeleteTarget.nodeid]);
      toast.success("Approval document deleted.");
      setApprovalDeleteTarget(null);
      await loadReport();
    } catch (e: any) {
      console.error("Failed to delete approval document", e);
      toast.error(e?.message || "Failed to delete approval document.");
    } finally {
      setDeletingApproval(false);
    }
  }, [approvalDeleteTarget, loadReport]);

  const handleApprovalUpload = useCallback(async () => {
    const uploadable = approvalUploads.filter((attachment) => attachment.file);

    if (!uploadable.length) {
      toast.error("Please choose a document to upload.");
      return;
    }

    setUploadingApproval(true);
    try {
      // Approval documents are regular attachments, tagged with a specifier
      // so they can be listed separately from generated report documents.
      const uploadsWithSpecifier = uploadable.map((attachment) => ({
        ...attachment,
        specifier: TEST_REPORT_APPROVAL_SPECIFIER,
        file: Object.assign(attachment.file as File, {
          title: attachment.title,
          description: attachment.description,
          specifier: TEST_REPORT_APPROVAL_SPECIFIER,
        }),
      }));

      await uploadAttachments(reportnodeid, uploadsWithSpecifier, []);
      setApprovalUploads([]);
      toast.success("Approval document uploaded.");
      await loadReport();
    } catch (e: any) {
      console.error("Failed to upload approval document", e);
      toast.error(e?.message || "Failed to upload approval document.");
    } finally {
      setUploadingApproval(false);
    }
  }, [approvalUploads, uploadAttachments, reportnodeid, loadReport]);

  const handleSaveAcceptanceCriteria = useCallback(async () => {
    setSavingAcceptanceCriteria(true);
    try {
      await updateTestReportAPI({
        nodeid: reportnodeid,
        acceptancecriteria: acceptanceCriteria,
      });
      toast.success("Acceptance criteria saved.");
    } catch (e: any) {
      console.error("Failed to save acceptance criteria", e);
      toast.error(e?.message || "Failed to save acceptance criteria.");
    } finally {
      setSavingAcceptanceCriteria(false);
    }
  }, [reportnodeid, acceptanceCriteria]);

  const handleGenerate = useCallback(async () => {
    setError(null);
    setGenerating(true);

    try {
      await postGenerateTestReport(reportnodeid, {
        options,
      });
      toast.success("Test report generation finished.");
      await loadReport();
    } catch (e: any) {
      console.error(e);
      setError(e?.message || "Failed to generate test report.");
      toast.error(e?.message || "Failed to generate test report.");
    } finally {
      setGenerating(false);
    }
  }, [reportnodeid, options, loadReport]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  return (
    <Paper sx={{ p: 2 }} elevation={3}>
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 3,
          bgcolor: "background.paper",
          pb: 2,
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          alignItems={{ xs: "stretch", md: "center" }}
          justifyContent="space-between"
          spacing={2}
        >
          <Typography variant="h6">Test Report Documents</Typography>

          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              onClick={handleGenerate}
              disabled={generating || loading}
            >
              {generating ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={18} color="inherit" />
                  <span>Generating...</span>
                </Stack>
              ) : (
                "Generate test report"
              )}
            </Button>

            <Tooltip title="Reload test report documents">
              <span>
                <IconButton onClick={loadReport} disabled={loading}>
                  <RefreshIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        <Tabs
          value={tabIndex}
          onChange={(_, value) => setTabIndex(value)}
          sx={{ mt: 2, borderBottom: 1, borderColor: "divider" }}
        >
          <Tab label="Test Reports" />
          <Tab label="DUT images" />
          <Tab label="Approval documents" />
          <Tab label="Acceptance criteria" />
          <Tab label="Options" />
        </Tabs>
      </Box>

      {error && (
        <Box mt={2}>
          <Alert severity="error">{error}</Alert>
        </Box>
      )}

      {loading && (
        <Box mt={4} textAlign="center">
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Loading test report documents...
          </Typography>
        </Box>
      )}

      {!loading && tabIndex === 0 && (
        <Box mt={3}>
          <AttachmentManager
            _attachments={reportDocuments}
            onChange={() => {}}
            disableFileChangeForExisting
            defaultExpanded
            allowedFileExtensions={[]}
            cropFeature={false}
            readOnly
            downloadAble
          />

          {!reportDocuments.length && !error && (
            <Box mt={3} textAlign="center" color="text.secondary">
              <Typography variant="body2">
                No test report documents yet. Generate the test report to create
                documents.
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {!loading && tabIndex === 1 && (
        <Box mt={3}>
          <AttachmentManager
            _attachments={combinedDutAttachments}
            onChange={handleDutAttachmentsChange}
            disableFileChangeForExisting
            defaultExpanded
            allowedFileExtensions={IMAGE_EXTS}
            cropFeature={false}
            previewImages
            showImageThumbs
            downloadAble
            title="DUT images"
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleDutImageUpload}
              disabled={!dutImageUploads.length || uploadingDutImages}
            >
              {uploadingDutImages ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Upload
            </Button>
          </Box>

          <DeletePopUp
            open={!!dutImageDeleteTarget}
            handleClose={() =>
              !deletingDutImage && setDutImageDeleteTarget(null)
            }
            handleDelete={handleConfirmDutImageDelete}
          />
        </Box>
      )}

      {!loading && tabIndex === 2 && (
        <Box mt={3}>
          <AttachmentManager
            _attachments={combinedApprovalAttachments}
            onChange={handleApprovalAttachmentsChange}
            disableFileChangeForExisting
            defaultExpanded
            allowedFileExtensions={PDF_EXTS}
            cropFeature={false}
            downloadAble
            title="Approval documents"
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleApprovalUpload}
              disabled={!approvalUploads.length || uploadingApproval}
            >
              {uploadingApproval ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Upload
            </Button>
          </Box>

          <DeletePopUp
            open={!!approvalDeleteTarget}
            handleClose={() =>
              !deletingApproval && setApprovalDeleteTarget(null)
            }
            handleDelete={handleConfirmApprovalDelete}
          />
        </Box>
      )}

      {!loading && tabIndex === 3 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6">Acceptance criteria</Typography>

          <TextField
            fullWidth
            multiline
            minRows={7}
            maxRows={7}
            sx={{ mt: 2 }}
            value={acceptanceCriteria}
            onChange={(e) => setAcceptanceCriteria(e.target.value)}
          />

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSaveAcceptanceCriteria}
              disabled={
                savingAcceptanceCriteria || !acceptanceCriteria.trim()
              }
            >
              {savingAcceptanceCriteria ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              Save
            </Button>
          </Box>
        </Paper>
      )}

      {!loading && tabIndex === 4 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 3 }}>
          <Typography variant="h6">Options</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Select the options that should be sent when generating the report.
          </Typography>

          <Stack spacing={1} sx={{ mt: 2 }}>
            {options.map((option) => (
              <FormControlLabel
                key={option.identifier}
                control={
                  <MUICheckbox
                    checked={option.value}
                    onChange={(event) =>
                      setOptions((current) =>
                        current.map((item) =>
                          item.identifier === option.identifier
                            ? { ...item, value: event.target.checked }
                            : item
                        )
                      )
                    }
                  />
                }
                label={option.label || option.identifier}
              />
            ))}
          </Stack>
        </Paper>
      )}
    </Paper>
  );
}
